import { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, ChevronDown, ChevronUp, Trash2, Search } from 'lucide-react';
import { FilterCheckboxProps } from '../types';
import { DrinkFlavor } from '@/types/firestore';
import type { ReduxSighting } from '@/store/slices/activeLocationSlice';
import { EditorInteractable } from '@/components/editor/EditorInteractable';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { collection, getDocs, orderBy, query, doc, deleteDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import StarRating from '@/components/ui/star-rating';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAppSelector } from '@/store/hooks';
import { selectDrinkById } from '@/store/slices/globalDataSlice';
import type { Drink as ReduxDrink } from '@/types/firestore';

// Import Search and DrinkDetailsModal
import DrinkDetailsModal from '@/components/drinks/DrinkDetailsModal';

// Type for confirmation
interface Confirmation {
  id: string;
  timestamp: number;
  userId: string;
  username: string;
  createdAt: number;
}

// Type for the grouped drink object passed to DrinkItem
interface GroupedDrinkDisplay {
    id: string; // Drink ID
    brandId: string;
    sightingId: string; // The ID of one of the sightings for this drink group
    sightingCount: number; // This should now be reliably updated from a Redux source via props
    lastSighted?: Date;
    brandName: string;
    name?: string; // Drink name
    flavors?: DrinkFlavor[];
    attributes?: Array<{ id?: string; name: string }>; // Ensure attributes has a name property
    sightingData?: ReduxSighting; // This ReduxSighting should be the source for sightingCount
    averageRating?: number;
    ratingCount?: number;
}

// Type for the selected drink object passed to DrinkDetails - This will be largely replaced
// We might still need parts of it for the popover, but the data should come from GroupedDrinkDisplay
// type SelectedDrinkDisplay = Drink & { 
//     sightingId: string; 
//     sightingData: ReduxSighting;
//     brandName: string;
//     drinkId: string;
// };

interface DrinkItemProps {
  drink: GroupedDrinkDisplay;
  // isSelected: boolean; // No longer needed if each item manages its own popover
  // handleSightingSelect: (sightingId: string) => void; // Will be replaced by popover trigger

  // Props for the popover content (confirmation logic)
  user: any; // Replace with Firebase User type
  onConfirmSighting: (sightingId: string, drinkId: string) => Promise<void>; // Modified to take IDs
  isConfirmingSightingId: string | null; // To track which item's confirmation is loading
  // confirmedTodayForSighting: (sightingId: string) => boolean; // To check if a specific sighting was confirmed today
  // Instead of passing a function, let's pass the direct data for "confirmed today"
  // This will require logic in StandardLocationView to prepare this per sighting if lastConfirmed is on sightingData
  isConfirmedToday: boolean; 
  locationId: string; // Needed for reportSighting call
  // averageRating?: number; // Removed - DrinkItem will fetch this
  // ratingCount?: number; // Removed - DrinkItem will fetch this
}

interface FilterCheckboxPropsExtended extends FilterCheckboxProps {
  handleToggle: (id: string) => void;
}

// --- Sub-components ---

// Drink Item Component
export const DrinkItem = memo(({ 
  drink,
  user,
  onConfirmSighting,
  isConfirmingSightingId,
  isConfirmedToday,
}: DrinkItemProps) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  // const [confirmationsModalOpen, setConfirmationsModalOpen] = useState(false); // This state seems unused now
  const [confirmationsOpen, setConfirmationsOpen] = useState(false);
  const [confirmations, setConfirmations] = useState<Confirmation[]>([]);
  const [loadingConfirmations, setLoadingConfirmations] = useState(false);
  const [isDeletingConfirmation, setIsDeletingConfirmation] = useState(false);
  const [confirmationToDelete, setConfirmationToDelete] = useState<Confirmation | null>(null);
  const [showDrinkDetailsModal, setShowDrinkDetailsModal] = useState(false);
  
  // Fetch live drink data from Redux store for ratings
  const liveDrinkData = useAppSelector(state => selectDrinkById(state, drink.id)) as ReduxDrink | undefined;
  const averageRating = liveDrinkData?.averageRating || 0;
  const ratingCount = liveDrinkData?.reviewCount || 0; // Firestore model uses reviewCount for drinks
  
  // currentConfirmationCount now directly reflects the prop
  // No local listener for sighting document count needed here anymore.
  // Responsibility shifts to parent ensuring `drink.sightingCount` is up-to-date from Redux.
  const currentConfirmationCount = drink.sightingCount || 0;

  // Fetch confirmations for the list when popover/collapsible opens
  useEffect(() => {
    if (popoverOpen && confirmationsOpen && drink.sightingData?.id) {
      fetchConfirmations();
    }
  }, [popoverOpen, confirmationsOpen, drink.sightingData?.id]);

  const fetchConfirmations = async () => {
    const sightingId = drink.sightingData?.id;
    if (!sightingId) return;
    
    setLoadingConfirmations(true);
    try {
      const confirmationsRef = collection(db, 'sightings', sightingId, 'confirmations');
      const q = query(confirmationsRef, orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedConfirmationsData: Confirmation[] = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          timestamp: data.timestamp?.toMillis() || 0,
          userId: data.userId || '',
          username: data.username || 'Unknown User',
          createdAt: data.createdAt?.toMillis() || 0
        };
      });
      
      setConfirmations(fetchedConfirmationsData);
    } catch (error) {
      console.error("Error fetching confirmations:", error);
    } finally {
      setLoadingConfirmations(false);
    }
  };

  const handleConfirm = async () => {
    if (!drink.sightingData?.drinkId || !drink.sightingData?.id) return;
    await onConfirmSighting(drink.sightingData.id, drink.sightingData.drinkId);
    // Fetching local confirmations list is still fine for the popover display
    fetchConfirmations(); 
    // The actual sightingCount update will come via props from Redux
  };

  const handleDeleteConfirmation = async () => {
    if (!confirmationToDelete || !drink.sightingData?.id) return;
    
    try {
      setIsDeletingConfirmation(true);
      const sightingId = drink.sightingData.id;
      const confirmationId = confirmationToDelete.id;
      
      await deleteDoc(doc(db, 'sightings', sightingId, 'confirmations', confirmationId));
      // Optimistically update the local list of confirmations shown in the popover
      setConfirmations(prev => prev.filter(c => c.id !== confirmationId));
      // The overall sightingCount will be updated via props from Redux
      setConfirmationToDelete(null);
    } catch (error) {
      console.error("Error deleting confirmation:", error);
    } finally {
      setIsDeletingConfirmation(false);
    }
  };
  
  const isCurrentlyConfirming = isConfirmingSightingId === drink.sightingData?.id;

  const displayText = drink.name
    ? <>{drink.brandName && <i>{drink.brandName}</i>} {drink.name} (<b>{currentConfirmationCount}</b>)</>
    : <>{drink.brandName && <i>{drink.brandName}</i>} (<b>{currentConfirmationCount}</b>)</>;

  const handleTrashCanClick = (e: React.MouseEvent, conf: Confirmation) => {
    e.preventDefault(); 
    e.stopPropagation(); 
    setConfirmationToDelete(conf); 
    requestAnimationFrame(() => {
        setPopoverOpen(true);
    });
  };

  const onPopoverOpenChange = (open: boolean) => {
    if (!confirmationToDelete) {
      setPopoverOpen(open);
    }
  };

  // const hasRating = averageRating > 0 && ratingCount > 0; // We will use this inside the popover only

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={onPopoverOpenChange}>
        <PopoverTrigger asChild>
          <div 
            className={`py-1 px-1.5 cursor-pointer text-sm hover:bg-muted rounded-md w-full text-left flex items-center gap-2`}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation(); // Prevent Popover from opening
                setShowDrinkDetailsModal(true);
              }}
              className="p-1 text-muted-foreground hover:text-primary rounded-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              aria-label="View drink details"
            >
              <Search className="h-3.5 w-3.5" />
            </button>
            <span className="flex-grow">{displayText}</span>
          </div>
        </PopoverTrigger>
        <PopoverContent 
          className="w-72 p-0" 
          side="top" 
          align="start"
          onInteractOutside={(e) => {
            if (confirmationToDelete) {
              e.preventDefault();
            }
          }}
        >
          <div className="p-3 space-y-3">
            <EditorInteractable entityId={drink.id} entityType="drink">
              {averageRating > 0 && ratingCount > 0 && (
                <div className="flex items-center mb-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <StarRating 
                            value={averageRating} 
                            readOnly 
                            size="sm"
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs p-2">
                        <p>{ratingCount} rating{ratingCount !== 1 ? 's' : ''}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
              <h5 className="font-medium text-base">
                <span className="text-muted-foreground">{drink.brandName || 'Unknown Brand'}</span>
                {' '}
                <span className="text-primary">{drink.name || 'Unknown Drink'}</span>
              </h5>
            </EditorInteractable>

            {((drink.flavors && drink.flavors.length > 0) || (drink.attributes && drink.attributes.length > 0)) && (
              <div className="flex flex-wrap gap-1 pt-1">
                {drink.flavors?.slice(0, 5).map((flavor) => (
                  flavor.id && flavor.name && <Badge key={flavor.id} variant="secondary" className="text-xs font-normal">{flavor.name}</Badge>
                ))}
                {drink.flavors && drink.flavors.length > 5 && <Badge variant="outline" className="text-xs font-normal">+ {drink.flavors.length - 5} more</Badge>}
                
                {drink.attributes?.slice(0, 5).map((attr, index) => (
                  attr.name && <Badge key={attr.id || `attr-${index}`} variant="outline" className="text-xs font-normal">{attr.name}</Badge>
                ))}
                {drink.attributes && drink.attributes.length > 5 && <Badge variant="outline" className="text-xs font-normal">+ {drink.attributes.length - 5} more</Badge>}
              </div>
            )}

            <div className="border-t pt-3 space-y-2">
              <Collapsible 
                open={confirmationsOpen} 
                onOpenChange={setConfirmationsOpen}
                className="border rounded-md"
              >
                <CollapsibleTrigger className="flex w-full items-center justify-between p-2 hover:bg-muted/50 rounded-t-md">
                  <span className="text-sm font-medium">
                    {currentConfirmationCount} confirmed sighting{currentConfirmationCount !== 1 ? 's' : ''}
                  </span>
                  {confirmationsOpen ? 
                    <ChevronUp className="h-4 w-4" /> : 
                    <ChevronDown className="h-4 w-4" />
                  }
                </CollapsibleTrigger>
                <CollapsibleContent className="px-2 pb-2">
                  <div className="space-y-1 mb-3 max-h-40 overflow-y-auto">
                    {loadingConfirmations ? (
                      <div className="flex justify-center items-center py-2">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-xs text-muted-foreground">Loading...</span>
                      </div>
                    ) : confirmations.length > 0 ? (
                      confirmations.map((conf) => (
                        <div key={conf.id} className="flex justify-between items-center text-xs py-1 border-b border-border/50 last:border-0">
                          <span>{new Date(conf.timestamp).toLocaleDateString()}</span>
                          <div className="flex items-center">
                            <span className="text-muted-foreground">{conf.username}</span>
                            <PermissionGuard 
                              requiredPermission="editor" 
                              userId={conf.userId}
                            >
                              <button
                                type="button"
                                onClick={(e) => handleTrashCanClick(e, conf)}
                                className="ml-2 text-muted-foreground hover:text-destructive p-1 rounded-sm"
                                aria-label="Delete confirmation"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </PermissionGuard>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-center text-muted-foreground py-2">
                        No detailed confirmation data available
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
                
                <div className="flex items-center justify-between p-2 border-t">
                  <div className="text-xs text-muted-foreground">
                    {drink.lastSighted && (
                      <span>Last: {drink.lastSighted.toLocaleDateString()}</span>
                    )}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs"
                    onClick={handleConfirm}
                    disabled={isCurrentlyConfirming || !user || isConfirmedToday || !drink.sightingData?.drinkId}
                  >
                    {isConfirmedToday ? (
                      "Confirmed"
                    ) : isCurrentlyConfirming ? (
                      <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Confirming...</>
                    ) : !user ? "Login" : "Confirm!"}
                  </Button>
                </div>
              </Collapsible>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <AlertDialog 
        open={!!confirmationToDelete} 
        onOpenChange={(open) => {
          if (!open) setConfirmationToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Confirmation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this confirmation record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmationToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirmation}
              disabled={isDeletingConfirmation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingConfirmation ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Deleting...</>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showDrinkDetailsModal && drink && (
        <DrinkDetailsModal 
          drinkId={drink.id} 
          onClose={() => setShowDrinkDetailsModal(false)} 
        />
      )}
    </>
  );
});

DrinkItem.displayName = 'DrinkItem';

// Drink Details Component - This will be removed as its functionality is merged into DrinkItem's Popover
/*
export const DrinkDetails = memo(({ 
// ... content of old DrinkDetails
}));
DrinkDetails.displayName = 'DrinkDetails';
*/

// Filter Checkbox Component
export const FilterCheckbox = memo(({ 
  id, 
  name, 
  checked, 
  filterId,
  handleToggle
}: FilterCheckboxPropsExtended) => {
  const handleChange = useCallback(() => {
    handleToggle(filterId);
  }, [handleToggle, filterId]);

  return (
    <div className="flex items-center space-x-2 mb-1">
      <Checkbox 
        id={id}
        checked={checked} 
        onCheckedChange={handleChange}
      />
      <label htmlFor={id} className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
        {name}
      </label>
    </div>
  );
});

FilterCheckbox.displayName = 'FilterCheckbox';

// --- Main DrinksList Component ---

// Updated Props Interface
export interface DrinksListProps {
  groupedDrinks: Record<string, GroupedDrinkDisplay>;
  drinksLoading: boolean;    
  user: any; // User object
  handleConfirmSighting: (sightingId: string, drinkId: string) => Promise<void>; 
  isConfirmingSightingId: string | null; // ID of the sighting being confirmed
  locationId: string; 
}

export const DrinksList = memo(({
  groupedDrinks,
  drinksLoading,
  user,
  handleConfirmSighting, // This function is (sightingId, drinkId) => Promise<void>
  isConfirmingSightingId,
  locationId, // locationId is available here
}: DrinksListProps) => {
  // Create a local state copy of the grouped drinks to update confirmation counts
  const [localGroupedDrinks, setLocalGroupedDrinks] = useState(groupedDrinks);

  // Update local state when props change
  useEffect(() => {
    setLocalGroupedDrinks(groupedDrinks);
  }, [groupedDrinks]);

  const drinkItems = useMemo(() => {
    return Object.values(localGroupedDrinks).sort((a, b) => {
        const nameA = `${a.brandName} ${a.name}`;
        const nameB = `${b.brandName} ${b.name}`;
        return nameA.localeCompare(nameB);
    });
  }, [localGroupedDrinks]);

  if (drinksLoading) {
    return (
      <div className="p-4 flex justify-center items-center min-h-[100px]">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        <span className="text-muted-foreground">Loading sightings...</span>
      </div>
    );
  }

  if (drinkItems.length === 0) {
    return <div className="text-sm text-muted-foreground text-center py-4">No sightings yet for this location.</div>;
  }

  return (
    <div className="space-y-0 border border-border rounded-md max-h-[280px] overflow-y-auto p-2 mt-2"> 
      {drinkItems.map((drink) => {
        // Logic to determine if *this specific drink's sighting* was confirmed today
        const confirmedTodayForThisItem = (() => {
            const lastConfirmedTimestamp = drink.sightingData?.lastConfirmed;
            if (!lastConfirmedTimestamp) return false;
            const lastConfirmedDate = new Date(lastConfirmedTimestamp);
            const today = new Date();
            return (
              lastConfirmedDate.getDate() === today.getDate() &&
              lastConfirmedDate.getMonth() === today.getMonth() &&
              lastConfirmedDate.getFullYear() === today.getFullYear()
            );
        })();

        return (
            <DrinkItem
              key={drink.sightingData?.id || drink.id} // Prefer sightingData.id for key
              drink={drink}
              user={user}
              onConfirmSighting={async (sightingIdFromItem, drinkIdFromItem) => {
                 await handleConfirmSighting(sightingIdFromItem, drinkIdFromItem);
              }}
              isConfirmingSightingId={isConfirmingSightingId}
              isConfirmedToday={confirmedTodayForThisItem} 
              locationId={locationId} // Pass locationId down to DrinkItem
            />
        );
      })}
    </div>
  );
});

DrinksList.displayName = 'DrinksList'; 